import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { API_URL, todayISO } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FileDown, FileText, Sheet } from "lucide-react";
import { toast } from "sonner";

export default function ReportsPage({ embedded = false }) {
  const { activeOrgId } = useAuth();
  const [start, setStart] = useState(`${new Date().getFullYear()}-01-01`);
  const [end, setEnd] = useState(todayISO());

  const downloadFile = async (path, filename) => {
    const token = localStorage.getItem("ledger_token") || "";
    try {
      const res = await fetch(`${API_URL}${path}`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Downloaded");
    } catch {
      toast.error("Failed to generate report");
    }
  };

  const downloadCSV = () => downloadFile(
    `/organizations/${activeOrgId}/reports/transactions.csv?start_date=${start}&end_date=${end}`,
    `transactions-${start}_to_${end}.csv`,
  );

  const downloadPL = () => downloadFile(
    `/organizations/${activeOrgId}/reports/profit-loss.pdf?start_date=${start}&end_date=${end}`,
    `profit-loss-${start}_to_${end}.pdf`,
  );

  return (
    <div className={embedded ? "space-y-6 max-w-3xl" : "space-y-8 fade-up max-w-3xl"}>
      {!embedded && (
        <header>
          <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">Reports</div>
          <h1 className="font-serif text-5xl tracking-tight mt-2">Export & share</h1>
          <p className="text-muted-foreground mt-2 max-w-lg">
            Configure a date range, then export your records as a CSV ledger or a polished P&L PDF.
          </p>
        </header>
      )}

      <Card className="border-border shadow-none p-7">
        <h3 className="font-serif text-2xl mb-1">Date range</h3>
        <p className="text-sm text-muted-foreground">Defaults to year-to-date.</p>
        <div className="grid grid-cols-2 gap-4 mt-5">
          <div className="space-y-1.5">
            <Label>Start date</Label>
            <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="report-start-date" />
          </div>
          <div className="space-y-1.5">
            <Label>End date</Label>
            <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="report-end-date" />
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Card className="border-border shadow-none p-6 transition-all duration-200 hover:shadow-md">
          <Sheet className="w-7 h-7 text-primary" />
          <h4 className="font-serif text-2xl mt-4">All transactions (CSV)</h4>
          <p className="text-sm text-muted-foreground mt-1">
            Every transaction with date, type, category, donor, and amount — perfect for bookkeepers and spreadsheets.
          </p>
          <Button className="mt-5" onClick={downloadCSV} data-testid="export-csv-button">
            <FileDown className="w-4 h-4 mr-2" /> Download CSV
          </Button>
        </Card>

        <Card className="border-border shadow-none p-6 transition-all duration-200 hover:shadow-md">
          <FileText className="w-7 h-7 text-primary" />
          <h4 className="font-serif text-2xl mt-4">Profit & Loss (PDF)</h4>
          <p className="text-sm text-muted-foreground mt-1">
            Income and expenses summarized by category, with net position for the period.
          </p>
          <Button className="mt-5" onClick={downloadPL} data-testid="export-pl-pdf-button">
            <FileDown className="w-4 h-4 mr-2" /> Download P&L PDF
          </Button>
        </Card>
      </div>

      <Card className="border-border shadow-none p-6 bg-secondary/40">
        <p className="text-sm text-foreground/80">
          Donor giving statements are generated per donor from the <strong>Donors</strong> page.
        </p>
      </Card>
    </div>
  );
}
